import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ApplicationPermissionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { IListFilterItem, ListFilterItem } from "source/models/common/listFilterItem";
import { ISavedListFilter } from "source/models/common/savedListFilter";
import { ITypedOption } from "source/models/common/typedOption";
import { recordLockManager } from "source/models/services/recordLockService";
import * as userService from "source/models/services/userService";
import { IAppSessionState } from "source/models/store/storeTypes";
import { IUnitNodePermissions } from "source/models/unit/unitNodePermissions";
import { IUsersPage } from "source/models/user/usersPage";
import { HeaderMenuItem, NavigationMenu } from "source/components/app/NavigationMenu";
import { UserMenu } from "source/components/app/UserMenu";
import { ListFilterSaveDialog } from "source/components/framework/Filters/ListFilterSaveDialog";
import { PageContainer } from "source/components/framework/Page/PageContainer";
import { PageContent } from "source/components/framework/Page/PageContent";
import { PageTopBar } from "source/components/framework/Page/PageTopBar";
import { FilterTabs } from "source/components/framework/PageNavMenuItem/FilterTabs";
import { UnitPermissionDialog } from "source/components/unit/Unit/UnitPermissionDialog";
import { UserFilterDrawer } from "../UserFilterDrawer";
import { UserList } from "../UserList";
import { IPageNavMenuItemActionsAction } from "source/components/framework/PageNavMenuItem/Actions";
import { abortManager } from "source/models/services/tokenService";

/* eslint-disable no-unused-vars */
const enum UserPageActivityFilterId {
    None = "0",
    Active = "1",
    InActive = "2",
    Expired = "3",
    NoUserGroup = "4",
    QrCodeUser = "5",
}
/* eslint-enable no-unused-vars */

// UserPage
export interface IUserPageOwnProps {
}

export interface IUserPageStateProps {
    appState: IAppSessionState;
    dataId: number;
    usersPage: IUsersPage;
}

export interface IUserPageDispatchProps {
    onGetUsersPage: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

type UserPageProp = IUserPageOwnProps & IUserPageStateProps & IUserPageDispatchProps;

interface IUserPageState {
    initialized: boolean;
    userGroups: IIdTitleNode[];
    showNavPanel: boolean;
    activeCompanyId: string;
    selectedUserGroupIds: string[];
    selectedActivityFilterId: string;
    activityFilters: IIdTitle[];
    sortColumn: string;
    sortOrderIsAsc: boolean;
    settingsActions: IPageNavMenuItemActionsAction[];
    // Filters
    userFilterIsSet: boolean;
    showUserFilterDialog: boolean;
    userFilterDialogFilter: IListFilterItem;
    filters: IListFilterItem[];
    savedFilters: IListFilterItem[];
    activeFilterId: string;
    activeSavedFilterId: string;
    showListFilterSaveDialog: boolean;
    listFilterSaveDialogFilter: IListFilterItem;
    listFilterSaveDialogGeneralFilter: any;
    listFilterSaveDialogReservedNames: string[];
    listFilterSaveDialogOnGetGeneralFilter: () => any;
    // Properties
    dataId: number;
    showUnitNodePermissionsDialog: boolean;
    unitNodePermissions: IUnitNodePermissions;
}

export class UserPage extends React.Component<UserPageProp, IUserPageState> {
    getActivityFilters = (): IIdTitle[] => {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(UserPageActivityFilterId.None, "'" + Translations.All + "'"));
        result.push(IdTitle.createIdTitle(UserPageActivityFilterId.Active, Translations.UserFilterActive));
        result.push(IdTitle.createIdTitle(UserPageActivityFilterId.InActive, Translations.UserFilterInActive));
        result.push(IdTitle.createIdTitle(UserPageActivityFilterId.Expired, Translations.UserFilterExpired));
        result.push(IdTitle.createIdTitle(UserPageActivityFilterId.NoUserGroup, Translations.UserFilterNoUserGroup));
        result.push(IdTitle.createIdTitle(UserPageActivityFilterId.QrCodeUser, Translations.UserFilterQrCodeUser));
        return result;
    };
    
    getFilters = (): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        result.push(ListFilterItem.createListFilterItem(Translations.Users, Translations.Users,
            {
                activityFilterId: UserPageActivityFilterId.None,
                userGroupIds: [Base.emptyGuid]
            }, "code", true));
        result.push(ListFilterItem.createListFilterItem(Translations.QrCodeUsers, Translations.QrCodeUsers,
            {
                activityFilterId: UserPageActivityFilterId.QrCodeUser,
                userGroupIds: [Base.emptyGuid]
            }, "code", true));
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getSavedFilters = (props: UserPageProp): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        if (!props || !props.usersPage) return result;
        for (const companyFilter of props.usersPage.companyFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(companyFilter, true));
        }
        for (const userFilter of props.usersPage.userFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(userFilter, false));
        }
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getUserFilterIsSet = (state: IUserPageState): boolean => {
        return UserFilterDrawer.filterIsSet(state.activityFilters, state.userGroups,
            state.selectedActivityFilterId, state.selectedUserGroupIds);
    };

    getSettingsActions = (): IPageNavMenuItemActionsAction[] => {
        const props = this.props;
        const sessionState = props.appState.state;
        const result: IPageNavMenuItemActionsAction[] = [];
        if (sessionState.isCompanyAdmin) {
            result.push({ title: Translations.Permissions, onClick: this.handleOpenPermissions });
        }
        return result;
    };

    constructor(props: UserPageProp) {
        super(props);
        abortManager.abortAll(); //Abort all abortable calls regardless of module
        const sessionState = props.appState.state;
        const filters = this.getFilters();
        const filterProps = ListFilterItem.getFilterProps(filters[0], "", true, this.getStateFilterPropsFromGeneralFilter);
        const activityFilters = this.getActivityFilters();
        const state = {
            ...filterProps,
            initialized: !!props.usersPage,
            userGroups: props.usersPage ? props.usersPage.userGroups : [],
            showNavPanel: sessionState.getShowPageNavigation(),
            activeCompanyId: sessionState.companyId,
            activityFilters,
            selectedActivityFilterId: activityFilters[0].id,
            settingsActions: this.getSettingsActions(),
            // Filters
            userFilterIsSet: false,
            activeFilterId: filters[0].id,
            activeSavedFilterId: "",
            filters,
            savedFilters: this.getSavedFilters(props),
            showUserFilterDialog: false,
            userFilterDialogFilter: null,
            showListFilterSaveDialog: false,
            listFilterSaveDialogFilter: null,
            listFilterSaveDialogGeneralFilter: null,
            listFilterSaveDialogReservedNames: [],
            listFilterSaveDialogOnGetGeneralFilter: null,
            // Properties
            dataId: props.dataId,
            showUnitNodePermissionsDialog: false,
            unitNodePermissions: null
        };
        this.state = {
            ...state,
            userFilterIsSet: this.getUserFilterIsSet(state)
        };
    }

    componentDidMount(): void {
        const sessionState = this.props.appState.state;
        recordLockManager.unlockAll(sessionState?.companyId, sessionState?.code);
        this.props.onGetUsersPage();
    }

    componentDidUpdate(prevProps: UserPageProp, prevState: IUserPageState): void {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const prevCompanyId = prevProps.usersPage && prevProps.usersPage.companies.length > 0 ? prevProps.usersPage.companies[0].id : Base.emptyGuid;
        const companyId = props.usersPage && props.usersPage.companies.length > 0 ? props.usersPage.companies[0].id : Base.emptyGuid;
        if (prevProps.dataId === props.dataId && prevCompanyId === companyId &&
            Base.equalStrArrays(prevProps.usersPage ? prevProps.usersPage.userGroups.map(i => i.id) : [], props.usersPage ? props.usersPage.userGroups.map(i => i.id) : []) &&
            Base.equalStrArrays(prevProps.usersPage ? prevProps.usersPage.companies.map(i => i.id) : [], props.usersPage ? props.usersPage.companies.map(i => i.id) : [])) return;
        const savedFilters = this.getSavedFilters(props);
        const activeSavedFilterId = savedFilters.find(i => i.id === state.activeSavedFilterId)
            ? state.activeSavedFilterId
            : "";
        const stateChange = {
            dataId: props.dataId,
            initialized: !!props.usersPage,
            showNavPanel: state.initialized ? state.showNavPanel : sessionState.getShowPageNavigation(),
            activeCompanyId: companyId,
            userGroups: props.usersPage ? props.usersPage.userGroups : [],
            savedFilters,
            activeSavedFilterId
        };
        const newState = { ...state, ...stateChange };
        this.setState({
            ...stateChange,
            userFilterIsSet: this.getUserFilterIsSet(newState),
            settingsActions: this.getSettingsActions(),
        });
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };

    handleChangeActiveCompanyId = async (companyId: string) => {
        if (this.state.activeCompanyId === companyId) return;
        const usersPageUserGroups = await userService.getUsersPageUserGroups(companyId);
        this.setState({
            activeCompanyId: companyId,
            userGroups: usersPageUserGroups.userGroups,
            selectedUserGroupIds: [Base.emptyGuid]
        });
    };
    
    // #region Saved Filter
    getGeneralFilterFromState = (state: IUserPageState): any => {
        return {
            userGroupIds: state.selectedUserGroupIds ? state.selectedUserGroupIds.filter(i => i !== Base.emptyGuid) : [],
            activityFilterId: state.selectedActivityFilterId ? parseInt(state.selectedActivityFilterId, 10) : 0
        };
    };

    getStateFilterPropsFromGeneralFilter = (generalFilter: any): any => {
        return {
            selectedUserGroupIds: generalFilter && generalFilter.userGroupIds && generalFilter.userGroupIds.length > 0 ? generalFilter.userGroupIds : [Base.emptyGuid],
            selectedActivityFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.activityFilterId) ? generalFilter.activityFilterId.toString(10) : UserPageActivityFilterId.Active
        };
    };
    
    getFilterPropsWithState = (listItemFilter: IListFilterItem) => {
        const state = this.state;
        let result = {
            selectedUserGroupIds: state.selectedUserGroupIds,
            selectedActivityFilterId: state.selectedActivityFilterId,
            sortColumn: state.sortColumn,
            sortOrderIsAsc: state.sortOrderIsAsc
        };
        if (listItemFilter) {
            result = ListFilterItem.getFilterProps(listItemFilter, result.sortColumn, result.sortOrderIsAsc, this.getStateFilterPropsFromGeneralFilter);
        }
        return result;
    };

    handleChangeActiveFilter = (activeFilterId: string) => {
        const state = this.state;
        const filter = state.filters.find(i => i.id === activeFilterId);
        if (!filter) return;
        const filterProps = this.getFilterPropsWithState(filter);
        const activeSavedFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId: "",
            userFilterIsSet: this.getUserFilterIsSet(newState)
        });
    };

    handleChangeActiveSavedFilter = (activeSavedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === activeSavedFilterId);
        if (!savedFilter) return;
        const filterProps = this.getFilterPropsWithState(savedFilter);
        const activeFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId,
            userFilterIsSet: this.getUserFilterIsSet(newState)
        });
    };

    handleRemoveSavedFilter = async (savedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === savedFilterId);
        if (!savedFilter) return;
        const result = await ListFilterSaveDialog.removeListFilterFromDb(state.savedFilters, state.activeCompanyId, savedFilterId, savedFilter.isShared, ApplicationPermissionType.User);
        if (!result) return;
        const savedFilters = state.savedFilters.filter(i => i.id !== savedFilterId);
        let activeFilterId = "";
        let activeSavedFilterId = "";
        let newfilter: IListFilterItem = null;
        if (savedFilters.length > 0) {
            newfilter = savedFilters[0];
            activeSavedFilterId = newfilter.id;
        } else if (state.filters.length > 0) {
            newfilter = state.filters[0];
            activeFilterId = newfilter.id;
        }
        const filterProps = this.getFilterPropsWithState(newfilter);
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId, savedFilters };
        this.setState({
            ...filterProps,
            showUserFilterDialog: false,
            activeFilterId,
            activeSavedFilterId,
            savedFilters,
            userFilterIsSet: this.getUserFilterIsSet(newState)
        });
    };

    handleFilterSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        this.setState({
            sortColumn,
            sortOrderIsAsc,
        });
    };

    updateFiltersAfterSave = (orgFilterId: string, savedListFilter: ISavedListFilter, isShared: boolean) => {
        if (!savedListFilter) return;
        const state = this.state;
        const savedFilters = state.savedFilters.filter(i => i.id !== orgFilterId || i.isShared !== isShared);
        const filter = ListFilterItem.createListFilterItemFromSavedListFilter(savedListFilter, isShared);
        savedFilters.push(filter);
        ListFilterItem.sortListFilterItems(savedFilters);
        this.setState({
            showListFilterSaveDialog: false,
            savedFilters,
            activeFilterId: "",
            activeSavedFilterId: savedListFilter.id,
            userFilterDialogFilter: filter
        });
    };

    getActiveFilter = (): IListFilterItem => {
        const state = this.state;
        return state.activeFilterId
            ? state.filters.find(i => i.id === state.activeFilterId)
            : state.savedFilters.find(i => i.id === state.activeSavedFilterId);
    };

    handleSaveListFilter = async (saveAs: boolean, onGetGeneralFilter: () => any) => {
        const state = this.state;
        const filter = this.getActiveFilter();
        if (!filter) return;
        if (!saveAs) {
            const savedListFilter = await ListFilterSaveDialog.saveListFilterToDb(filter, this.getGeneralFilterFromState(state), state.sortColumn, state.sortOrderIsAsc, state.activeCompanyId, ApplicationPermissionType.User);
            this.updateFiltersAfterSave(filter.id, savedListFilter, filter.isShared);
        } else {
            this.setState({
                showListFilterSaveDialog: true,
                listFilterSaveDialogFilter: filter,
                listFilterSaveDialogReservedNames: state.savedFilters.map(i => i.id).concat(state.filters.map(i => i.title)),
                listFilterSaveDialogGeneralFilter: this.getGeneralFilterFromState(state),
                listFilterSaveDialogOnGetGeneralFilter: onGetGeneralFilter
            });
        }
    };

    handleSaveListFilterDialogOk = (savedListFilter: ISavedListFilter, isShared: boolean) => {
        this.updateFiltersAfterSave("", savedListFilter, isShared);
    };

    handleSaveListFilterDialogCancel = () => {
        this.setState({
            showListFilterSaveDialog: false
        });
    };
    // #endregion Saved Filter

    // #region Record Filter
    handleOpenUserFilterDialog = () => {
        const filter = this.getActiveFilter();
        if (!filter) return;
        this.setState({
            showUserFilterDialog: true,
            userFilterDialogFilter: filter
        });
    };

    onUserFilterDialogOk = (closeDialog: boolean, selectedActivityFilterId: string, selectedUserGroupIds: string[]): any => {
        const newState = { ...this.state, selectedActivityFilterId, selectedUserGroupIds };
        this.setState({
            showUserFilterDialog: !closeDialog,
            selectedActivityFilterId,
            selectedUserGroupIds,
            userFilterIsSet: this.getUserFilterIsSet(newState)
        });
        return this.getGeneralFilterFromState(newState);
    };
    
    onUserFilterDialogCancel = () => {
        this.setState({ showUserFilterDialog: false });
    };
    // #endregion Record Filter

    handleOpenPermissions = async () => {
        const result = await AppUtils.callService(() => userService.getUnitNodePermissions(this.state.activeCompanyId), false);
        if (!result) return;
        this.setState({
            showUnitNodePermissionsDialog: true,
            unitNodePermissions: result
        });
    };

    handlePermissionsDialogOk = () => {
        this.setState({
            dataId: Base.getRandomIntInteger(1000000),
            showUnitNodePermissionsDialog: false
        });
    };

    handlePermissionsDialogCancel = () => {
        this.setState({
            showUnitNodePermissionsDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const companies = props.usersPage ? props.usersPage.companies : [];
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.Users}
                    companies={companies}
                    activeCompanyId={state.activeCompanyId}
                    onChangeActiveCompany={this.handleChangeActiveCompanyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <UserList
                        filterName={state.activeSavedFilterId
                            ? state.savedFilters.find(i => i.id === state.activeSavedFilterId)?.title
                            : state.activeFilterId && state.activeFilterId !== Translations.Users
                                ? state.filters.find(i => i.id === state.activeFilterId)?.title
                                : null}
                        sessionState={sessionState}
                        dataId={state.dataId.toString(10)}
                        preloaded={true}
                        waitingPreloadedData={true}
                        list={props.usersPage ? props.usersPage.list : null}
                        listFilterIsSet={state.userFilterIsSet}
                        userGroupIds={state.selectedUserGroupIds}
                        companyId={state.activeCompanyId}
                        activityState={state.selectedActivityFilterId}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        settingsActions={state.settingsActions}
                        reportTabs={
                            <FilterTabs
                                items={state.savedFilters}
                                standardFilters={state.filters}
                                selectedId={this.state.activeFilterId}
                                selectedSavedId={this.state.activeSavedFilterId}
                                onSetActiveItem={this.handleChangeActiveSavedFilter}
                                onSetActiveStandardItem={this.handleChangeActiveFilter}
                            />
                        }
                        onRefreshSessionState={props.onRefreshSessionState}
                        onSetSortData={this.handleFilterSortData}
                        onOpenFiltersDialog={this.handleOpenUserFilterDialog}
                        onToggleSettingsDialog={this.handleOpenPermissions}
                        listFilterIsQrCodeFilter={state.activeFilterId === Translations.QrCodeUsers}
                        onChangeToQrCodeFilter={() => this.handleChangeActiveFilter(Translations.QrCodeUsers)}
                    />
                </PageContent>
                {state.showUnitNodePermissionsDialog &&
                    <UnitPermissionDialog
                        companyId={state.activeCompanyId}
                        unitNodePermissions={state.unitNodePermissions}
                        onSave={userService.saveUnitNodePermissions}
                        onOk={this.handlePermissionsDialogOk}
                        onCancel={this.handlePermissionsDialogCancel}
                    />
                }
                <UserFilterDrawer
                    open={state.showUserFilterDialog}
                    filter={state.userFilterDialogFilter}
                    userGroups={state.userGroups}
                    activityFilters={state.activityFilters}
                    selectedUserGroupIds={state.selectedUserGroupIds}
                    selectedActivityFilterId={state.selectedActivityFilterId}
                    canRemoveSharedFilters={sessionState.isCompanyAdmin}
                    onSaveFilter={this.handleSaveListFilter}
                    onRemoveFilter={this.handleRemoveSavedFilter}
                    onOk={this.onUserFilterDialogOk}
                    onCancel={this.onUserFilterDialogCancel}
                />
                {state.showListFilterSaveDialog &&
                    <ListFilterSaveDialog
                        sessionState={sessionState}
                        companyId={state.activeCompanyId}
                        applicationPermissionType={ApplicationPermissionType.User}
                        listFilter={state.listFilterSaveDialogFilter}
                        generalFilter={state.listFilterSaveDialogGeneralFilter}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        reservedNames={state.listFilterSaveDialogReservedNames}
                        onGetGeneralFilter={state.listFilterSaveDialogOnGetGeneralFilter}
                        onOk={this.handleSaveListFilterDialogOk}
                        onCancel={this.handleSaveListFilterDialogCancel}
                    />
                }
            </PageContainer>
        );
    }
}